import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';

import { translate } from '@jsverse/transloco';
import { Subject, takeUntil } from 'rxjs';

import {
  AnalyticItemFilterSets,
  Filter,
  FilterService,
  FilterSet,
} from 'app/modules/collection/services/filter.service';
import { DashboardDatasourceFilterService } from 'app/modules/dashboard/services/dashboard-datasource-filter.service';
import { DatasourceManagerStore } from 'app/modules/dashboard/stores/datasource-manager.store';
import {
  DataSourceItem,
  DatasourceManager,
} from 'app/modules/widget/components/data-source-chooser/data-source-chooser.component';
import { Alias, AliasResourceType, AliasService } from 'app/providers/bridge-api/alias.service';
import { AuthService } from 'app/providers/bridge-api/auth.service';
import { DashboardService } from 'app/providers/bridge-api/dashboard.service';
import { SpotEService } from 'app/providers/semantria-api/spote.service';
import { AnalysisGroupService } from 'app/providers/storage-api/analysis-group-service';
import { Analysis, AnalysisService } from 'app/providers/storage-api/analysis.service';
import { StorageAccountService } from 'app/providers/storage-api/blender-account.service';
import { CollectionService } from 'app/providers/storage-api/collection.service';
import { FieldService } from 'app/providers/storage-api/field.service';
import { Calculation, PredictiveService } from 'app/providers/storage-api/predictive.service';
import { Project } from 'app/providers/storage-api/project.service';
import { AlertService } from 'app/services/alert.service';
import { LoggerService } from 'app/services/logger.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { SystemService } from 'app/services/system.service';
import { ObjectLabel, ItemLabel } from '../../../../providers/storage-api/object-label.service';
import {
  ActiveUse,
  DataSourcePillDisplay,
  ProjectDetailService,
  ProjectItem,
} from '../../services/project-detail.service';
import { ProjectItemActiveUseService } from '../../services/project-item-active-use.service';

@EnableClassTelemetry()
@Component({
  selector: 'app-project-details-item-panel',
  templateUrl: './project-details-item-panel.component.html',
  styleUrls: ['./project-details-item-panel.component.scss'],
  standalone: false,
})
export class ProjectDetailsItemPanelComponent implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() project: Project;
  @Input() isReadOnly: boolean;
  @Input() isExecutive: boolean;

  selectedItem: ProjectItem;
  isLoading: boolean;
  isHiddenVisible;
  activeUseLoading: boolean;

  currentTabId: string;

  note = '';
  icons: any;
  session;
  unsub$ = new Subject<void>();

  isNotesEditorOpen = false;
  dashboardRulesModalOpen = false;
  dataSources: DataSourceItem[];

  private projectDetailService = inject(ProjectDetailService);
  private predictiveService = inject(PredictiveService);
  private dashboardService = inject(DashboardService);
  private authService = inject(AuthService);
  private aliasService = inject(AliasService);
  private systemService = inject(SystemService);
  private alertService = inject(AlertService);
  private storageAccountService = inject(StorageAccountService);
  private analysisService = inject(AnalysisService);
  private spotEService = inject(SpotEService);
  private filterService = inject(FilterService);
  private activeUseService = inject(ProjectItemActiveUseService);
  private collectionService = inject(CollectionService);
  private analysisGroupService = inject(AnalysisGroupService);
  private dashboardDataSourceFilterService = inject(DashboardDatasourceFilterService);
  private readonly datasourceManagerStore = inject(DatasourceManagerStore);
  private readonly logger = inject(LoggerService);

  constructor() {}

  async ngOnInit() {
    this.session = await this.authService.getSession();
    this.icons = this.projectDetailService.projectItemIcons;
    this.projectDetailService.currentItem.pipe(takeUntil(this.unsub$)).subscribe(async (item) => {
      this.note = '';
      this.selectedItem = item;

      if (this.selectedItem) {
        if (this.selectedItem.type === 'collection') {
          this.getSmartSummaryScene(this.selectedItem.collection.data.summarySceneId);
          if (!this.selectedItem.isSocial) {
            this.getCollectionUsages(
              this.selectedItem,
              this.project,
              this.selectedItem.collection.data.collectionName
            );
          }
        }

        if (this.selectedItem.type === 'group') {
          this.getGroupUsages(this.selectedItem, this.project.id, this.selectedItem.group.id);
        }

        if (
          this.selectedItem.type === 'collection' &&
          this.selectedItem.displayData.isDriverAnalysis &&
          !this.selectedItem.collection.driverAnalyses.length
        ) {
          this.getKDA(this.project.id, this.selectedItem.collection.data.collectionName);
        }

        if (
          this.selectedItem.type === 'analysis' &&
          this.selectedItem.displayData.isDriverAnalysis &&
          !this.selectedItem.displayData.kda
        ) {
          await this.getKDA(this.project.id, this.selectedItem.calculation.collectionId);
        }
        if (this.selectedItem.type === 'analysis' && !this.selectedItem.isSocial) {
          this.getAnalysisUsages(this.project, this.selectedItem);
        }

        if (this.selectedItem.type === 'dashboard') {
          this.isHiddenVisible =
            this.authService.isAccountAdmin ||
            this.selectedItem.dashboard.user_id === this.session.info2.user_id;

          if (!this.selectedItem.displayData.aliases) {
            this.aliasService
              .getAliasForResource(AliasResourceType.DASHBOARD, this.selectedItem.id)
              .then((res) => (this.selectedItem.displayData.aliases = res.result.hits));
          }
          this.filterAndDisplayDataSources();
        }
        this.getNote();
      }
    });

    this.projectDetailService.currentTab
      .pipe(takeUntil(this.unsub$))
      .subscribe((data) => (this.currentTabId = data.tabId));
  }

  private loadDataSources(): DataSourceItem[] {
    const datasources = this.datasourceManagerStore.getDatasources();
    const dsManager = new DatasourceManager(datasources, '');
    return dsManager.search(null);
  }

  private filterAndDisplayDataSources() {
    this.dataSources = this.loadDataSources();

    const filters = this.selectedItem.dashboard.payload.dataSourceFilters;

    const filteredDataSources = this.dashboardDataSourceFilterService.filterDataSourceItems(
      filters,
      this.dataSources
    );
    const availableAnalyses: DataSourceItem[] = [];
    const availableAnalysisGroups: DataSourceItem[] = [];
    for (const ds of filteredDataSources) {
      if (
        ds.value.type === 'analysis' &&
        !this.selectedItem.displayData.analysisDataSources.some((ads) => ads.name === ds.value.id)
      ) {
        availableAnalyses.push(ds);
      } else if (
        ds.value.type === 'group' &&
        !this.selectedItem.displayData.groupDataSources.some((ads) => ads.id === ds.value.id)
      ) {
        availableAnalysisGroups.push(ds);
      }
    }

    this.selectedItem.displayData.dashboardFilterCount = this.getDashboardFilterCount();

    this.selectedItem.displayData.inUseDataSources = [
      ...this.selectedItem.displayData.analysisDataSources.map((ds) => {
        const collection = this.dataSources.find((dataSource) => dataSource.value.id === ds.name);
        return {
          id: ds.name,
          displayName: collection?.display ?? ds.displayName,
          isSocial: ds.isSocial,
          type: 'collection' as const,
          icon: ds.isSocial ? this.icons['socialCollection'].icon : this.icons.analysis.icon,
          color: ds.isSocial ? this.icons['socialCollection'].color : this.icons.analysis.color,
          inUse: true,
          filteredOutActiveUse: !filteredDataSources.some(
            (fds) => fds.value.analysisId === ds.name
          ),
          tabId: 'collection' as const,
          itemId: ds.collectionName,
          projectionId: ds.name,
        };
      }),
      ...this.selectedItem.displayData.groupDataSources.map((ds) => ({
        id: ds.id,
        displayName: ds.name,
        type: 'group' as const,
        icon: this.icons.group.icon,
        color: this.icons.group.color,
        inUse: true,
        filteredOutActiveUse: !filteredDataSources.some((fds) => fds.value.groupId === ds.id),
        tabId: 'group' as const,
        itemId: ds.id,
        projectionId: ds.analyses?.[0].name,
      })),
    ];
    this.selectedItem.displayData.availableDataSources = [
      ...availableAnalyses.map((ds) => ({
        id: ds.value.id,
        displayName: `${ds.value.collectionDisplayName} - (${ds.value.displayName})`,
        isSocial: ds.value.isSocial,
        type: 'collection' as const,
        icon: ds.value.isSocial ? this.icons['socialCollection'].icon : this.icons.analysis.icon,
        color: ds.value.isSocial ? this.icons['socialCollection'].color : this.icons.analysis.color,
        tabId: 'collection' as const,
        itemId: ds.value.collectionName,
        projectionId: ds.value.id,
      })),
      ...availableAnalysisGroups.map((ds) => ({
        id: ds.value.id,
        displayName: ds.value.displayName,
        type: 'group' as const,
        icon: this.icons.group.icon,
        color: this.icons.group.color,
        tabId: 'group' as const,
        itemId: ds.value.id,
        projectionId: ds.value.groupAnalyses?.[0]?.['name'],
      })),
    ];
  }

  ngOnDestroy(): void {
    this.unsub$.next();
    this.unsub$.complete();
  }

  private async getKDA(projectId, collectionName) {
    this.isLoading = true;

    await this.storageAccountService.groupFieldsSub.latest();

    try {
      if (this.selectedItem.collection) {
        const calculationList = await this.predictiveService.listCalculations(
          projectId,
          collectionName,
          {
            pageSize: 1024,
            sideloads: 'objectLabels',
          }
        );

        this.selectedItem.collection.driverAnalyses = calculationList.items;
      } else {
        const calculation = await this.predictiveService.getCalculationById(
          projectId,
          collectionName,
          this.selectedItem.id,
          {
            sideloads: 'objectLabels',
          }
        );

        this.selectedItem.displayData.kda = calculation;
        const calcProps = calculation.request.properties;
        this.selectedItem.displayData.kdaLabels = {
          breakdownColumn: FieldService.getFieldLongName(calculation.request.breakdownColumnLabel),
          dependentColumn: FieldService.getFieldLongName(calculation.request.dependentColumnLabel),
          driverColumns: calculation.request.driverColumnLabels.map((d) =>
            FieldService.getFieldLongName(d)
          ),
          breakdown:
            calculation.status !== 'ERROR' ? Object.keys(calculation.result?.breakdown) : undefined,
          docCount: calculation.result?.topline?.count,
          weightField: FieldService.getFieldLongName(calcProps.weightField),
          widgetFilters: calcProps['dashboard:filters']
            ? this.getKDAFilters(
                JSON.parse(calcProps['dashboard:filters']),
                undefined,
                calcProps.userSearch ? JSON.parse(calcProps.userSearch) : undefined
              )
            : null,
          widgetGroupFilters: calcProps['dashboard:widgetGroupFilters']
            ? this.getKDAFilters(JSON.parse(calcProps['dashboard:widgetGroupFilters']))
            : null,
          comparisonFilters: calcProps['dashboard:comparisonFilters']
            ? this.getKDAFilters(null, JSON.parse(calcProps['dashboard:comparisonFilters']))
            : null,
        };
      }
    } catch {
      if (!this.selectedItem) {
        this.logger.debug('No item selected');
      }
    }

    this.isLoading = false;
  }

  private getKDAFilters(
    filters: AnalyticItemFilterSets,
    comparisonFilter?: FilterSet,
    userSearch?: FilterSet
  ): { andFilters: string[]; orFilters: string[]; notFilters: string[] } {
    if (!comparisonFilter) {
      let andFilters: Filter[] = [];
      let orFilters: Filter[] = [];
      let notFilters: Filter[] = [];

      for (const key in filters) {
        if (filters.hasOwnProperty(key)) {
          const filterSet = filters[key] as FilterSet;

          andFilters = andFilters.concat(filterSet.and);
          orFilters = orFilters.concat(filterSet.or);
          notFilters = notFilters.concat(filterSet.not);
        }
      }

      const filtersDisplay = {
        andFilters: andFilters.map((i) => this.getKdaFilterDisplayLabel(i)),
        orFilters: orFilters.map((i) => this.getKdaFilterDisplayLabel(i)),
        notFilters: notFilters.map((i) => this.getKdaFilterDisplayLabel(i)),
      };

      if (userSearch) {
        filtersDisplay.andFilters.push(...userSearch.and.map((s) => s.displayValue));
        filtersDisplay.orFilters.push(...userSearch.or.map((s) => s.displayValue));
        filtersDisplay.notFilters.push(...userSearch.not.map((s) => s.displayValue));
      }

      if (
        filtersDisplay.andFilters.length > 0 ||
        filtersDisplay.orFilters.length > 0 ||
        filtersDisplay.notFilters.length > 0
      ) {
        return filtersDisplay;
      }
    } else {
      //Only 1 Comparison filter allowed
      if (comparisonFilter.and[0].value) {
        return { andFilters: [comparisonFilter.label], orFilters: [], notFilters: [] };
      }
    }

    return null;
  }

  private getKdaFilterDisplayLabel(filter: Filter): string {
    if (filter.analyticItem.startsWith('metadata.')) {
      const fieldLongName = FieldService.getFieldLongName(
        filter.analyticItem.replace(/^metadata\./, '')
      );
      return `${fieldLongName} ${filter.displayValue}`;
    }

    if (filter.analyticItem === 'createdAt') {
      return `Date: ${filter.displayValue}`;
    }

    return `${this.filterService.getFilterConfigItem(filter.analyticItem).title}: ${
      filter.displayValue
    }`;
  }

  private async getSmartSummaryScene(summaryId: string) {
    if (summaryId) {
      const scenes = await this.spotEService.listScenes({ type: 'DOCUMENT_SUMMARY' });
      const scene = scenes.data.find((s) => s.scene_id === summaryId);
      if (scene && this.selectedItem?.type === 'collection') {
        this.selectedItem.displayData.smartSummaryName = scene.name;
      }
    }
  }

  private async getAnalysisUsages(project: Project, selectedItem: ProjectItem) {
    this.activeUseLoading = true;
    selectedItem.displayData.isDriverAnalysis
      ? (this.selectedItem.displayData.activeUse = this.activeUseService.getDashboardActiveUseKDA(
          [selectedItem.calculation],
          project
        ))
      : (this.selectedItem = await this.activeUseService.getAnalysisUsages(selectedItem, project));

    this.activeUseLoading = false;
  }

  private async getCollectionUsages(
    selectedItem: ProjectItem,
    project: Project,
    collectionName: string
  ) {
    this.activeUseLoading = true;
    this.selectedItem = await this.activeUseService.getCollectionUsages(
      selectedItem,
      project,
      collectionName
    );
    this.activeUseLoading = false;
  }

  private async getGroupUsages(selectedItem: ProjectItem, projectId: string, groupId: string) {
    this.activeUseLoading = true;
    this.selectedItem = await this.activeUseService.getGroupUsages(
      selectedItem,
      projectId,
      groupId
    );
    this.activeUseLoading = false;
  }

  private getNote() {
    switch (this.selectedItem.type) {
      case 'dashboard':
        this.note = this.selectedItem.dashboard.payload.notes;
        break;
      case 'collection':
        this.note = this.selectedItem.collection.data.notes;
        break;
      case 'group':
        this.note = this.selectedItem.group.notes;
        break;
      case 'analysis':
        this.selectedItem.displayData.kda
          ? (this.note = this.selectedItem.displayData.kda?.notes)
          : (this.note = this.selectedItem.analysis?.notes);

        break;
    }
  }

  private async saveNote(item: ProjectItem) {
    switch (item.type) {
      case 'dashboard':
        item.dashboard.payload.notes = this.note;

        try {
          const d = await this.dashboardService.updateDashboard(item.dashboard);
          item.dashboard.modified_at_date = d.modified_at_date;
          item.dashboard.user_modified_at_date = d.user_modified_at_date;

          this.alertService.showToast(
            translate('project.project-details-item-panel.dashboard-updated')
          );
        } catch (error) {
          this.alertService.showErrorToast(
            translate('project.project-details-item-panel.dashboard-updated-error'),
            error
          );
        }
        break;
      case 'collection':
        item.collection.data.notes = this.note;

        try {
          await this.collectionService.updateCollection(item.collection.data);

          this.alertService.showToast(
            translate('project.project-details-item-panel.collection-updated')
          );
        } catch (e) {
          this.alertService.showErrorToast(
            translate('project.project-details-item-panel.collection-updated-failed'),
            e
          );
        }

        break;
      case 'group':
        item.group.notes = this.note;

        await this.analysisGroupService.updateAnalysisGroup(
          this.project.id,
          item.group.id,
          item.group
        );

        this.alertService.showToast(
          translate('project.project-details-item-panel.analysis-group-updated')
        );

        break;
      case 'analysis':
        const kda = item.displayData.kda;
        kda ? (item.displayData.kda.notes = this.note) : (item.analysis.notes = this.note);

        try {
          if (kda) {
            await this.predictiveService.updateDriverAnalysis(
              this.project.id,
              kda.collectionId,
              kda.id,
              kda.displayName,
              kda.notes
            );
          } else {
            await this.analysisService.updateAnalysis({
              projectId: this.project.id,
              collectionName: item.analysis.collectionName,
              analysisId: item.analysis.name,
              notes: item.analysis.notes,
              displayName: item.analysis.displayName,
            });

            this.projectDetailService.onAnalysisEvent('UPDATE', item.analysis);
          }
          this.alertService.showToast(
            translate('project.project-details-item-panel.analysis-updated')
          );
        } catch (e) {
          this.alertService.showErrorToast(
            translate('project.project-details-item-panel.analysis-updated-error'),
            e
          );
        }
        break;
    }
  }

  onCopyAlias(alias: Alias) {
    this.systemService.copyToClipboard(`${location.protocol}//${location.host}/d/${alias.name}`);
    this.alertService.showToast(translate('project.project-details-item-panel.alias-copied'));
  }

  onAnalysisClicked(analysis: Analysis) {
    const tabId = this.currentTabId === 'all' ? 'all' : 'collection';
    this.projectDetailService.changeCurrentTab(
      tabId,
      analysis.collectionName,
      analysis.name ?? analysis.analysisName
    );
  }

  onKDAClicked(analysis: Calculation) {
    const tabId = this.currentTabId === 'all' ? 'all' : 'collection';
    this.projectDetailService.changeCurrentTab(tabId, analysis.collectionId, analysis.id);
  }

  onDataSourceClicked(ds: DataSourcePillDisplay) {
    const tabId = this.currentTabId === 'all' ? 'all' : ds.tabId;
    this.projectDetailService.changeCurrentTab(tabId, ds.itemId, ds.projectionId);
  }

  onActiveUseClicked(activeUse: ActiveUse) {
    const tabId = this.currentTabId === 'all' ? 'all' : activeUse.activeUseType;
    const projectedItem = activeUse.activeUseType === 'group' ? activeUse.id : undefined;
    this.projectDetailService.changeCurrentTab(tabId, activeUse.id, projectedItem);
  }

  onOpenNotesEditor(): void {
    this.isNotesEditorOpen = true;
  }

  onCloseNotesEditor(notes: string | null): void {
    this.isNotesEditorOpen = false;

    if (notes !== null) {
      this.note = notes;

      this.saveNote(this.selectedItem);
    }
  }

  onLabelUpdated($event: Partial<ItemLabel>, item: ProjectItem) {
    const labelIdx = item.labels.findIndex((l) => l.id === $event.id);
    item.labels.splice(labelIdx, 1, $event);
    item.labels = [...item.labels];

    this.triggerDataSourceLabelsChanged(item);
  }

  onLabelApplied($event: Partial<ItemLabel>, item: ProjectItem) {
    item.labels = [...item.labels, $event];
    if (item.labels.length === 1) {
      this.projectDetailService.onLabelsExistenceChanged();
    }

    this.triggerDataSourceLabelsChanged(item);
  }

  onLabelRemoved($event: string, item: ProjectItem) {
    item.labels = item.labels.filter((l) => l.id !== $event);
    if (!item.labels.length) {
      this.projectDetailService.onLabelsExistenceChanged();
    }

    this.triggerDataSourceLabelsChanged(item);
  }

  private triggerDataSourceLabelsChanged(item: ProjectItem) {
    if (['analysis', 'group'].includes(item.type)) {
      if (!this.dataSources?.some((ds) => ds.value.id === item.id)) {
        this.dataSources = this.loadDataSources();
      }

      const ds = this.dataSources?.find((entry) => entry.value.id === item.id);
      if (ds) {
        ds.value.objectLabels = [...(item.labels as unknown as ObjectLabel[])];
      }

      if (this.dataSources) {
        const modalDs = this.dataSources.find((entry) => entry.value.id === item.id);
        if (modalDs) {
          modalDs.value.objectLabels = [...(item.labels as unknown as ObjectLabel[])];
        }
      }
    }

    if (item.type === 'collection') {
      const { collectionName } = item.collection.data;
      this.projectDetailService.onCollectionLabelsChanged(collectionName, item.labels);
    }

    if (item.type !== 'analysis' || item.displayData.isDriverAnalysis) {
      return;
    }
    this.projectDetailService.onAnalysisLabelsChanged(item.id, item.labels);
  }

  onOpenDashboardRulesModal() {
    this.dataSources = this.loadDataSources();
    this.dashboardRulesModalOpen = true;
  }

  onCloseDashboardRulesModal(result: boolean) {
    this.dashboardRulesModalOpen = false;
    if (result) {
      this.selectedItem.displayData.dashboardFilterCount = this.getDashboardFilterCount();
      this.dashboardService.removeCachedDashboard(
        this.selectedItem.dashboard.id,
        this.project.id,
        this.session.info2.user_id
      );
      this.filterAndDisplayDataSources();
    }
  }

  private getDashboardFilterCount() {
    const filters = this.selectedItem.dashboard.payload.dataSourceFilters;
    return ['and', 'or', 'not'].reduce((count, op) => count + (filters?.[op]?.length || 0), 0);
  }
}
